import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const AlbumContext = createContext();

export const useAlbums = () => {
  const context = useContext(AlbumContext);
  if (!context) {
    throw new Error('useAlbums must be used within an AlbumProvider');
  }
  return context;
};

export const AlbumProvider = ({ children }) => {
  const { user } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch user's albums
  const fetchUserAlbums = async () => {
    if (!user) return [];

    try {
      const { data, error } = await supabase
        .from('albums_ab2024')
        .select(`
          *,
          photos:photos_ab2024(*)
        `)
        .eq('owner_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to match existing structure
      const transformedAlbums = data.map(album => ({
        id: album.id,
        shareId: album.share_id,
        title: album.title,
        description: album.description,
        eventDate: album.event_date,
        isPublic: album.is_public,
        ownerId: album.owner_id,
        ownerName: album.owner_name,
        createdAt: album.created_at,
        updatedAt: album.updated_at,
        photos: album.photos.map(photo => ({
          id: photo.id,
          url: photo.url,
          name: photo.name,
          size: photo.size,
          type: photo.type,
          uploaderName: photo.uploader_name,
          uploaderId: photo.uploader_id,
          uploadedAt: photo.uploaded_at
        }))
      }));

      setAlbums(transformedAlbums);
      return transformedAlbums;
    } catch (error) {
      console.error('Error fetching albums:', error);
      return [];
    }
  };

  useEffect(() => {
    if (user) {
      fetchUserAlbums();
    } else {
      setAlbums([]);
    }
  }, [user]);

  const createAlbum = async (albumData) => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('albums_ab2024')
        .insert([{
          title: albumData.title,
          description: albumData.description,
          event_date: albumData.eventDate || null,
          is_public: albumData.isPublic,
          owner_id: user.id,
          owner_name: user.name
        }])
        .select()
        .single();

      if (error) throw error;

      const newAlbum = {
        id: data.id,
        shareId: data.share_id,
        title: data.title,
        description: data.description,
        eventDate: data.event_date,
        isPublic: data.is_public,
        ownerId: data.owner_id,
        ownerName: data.owner_name,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        photos: []
      };

      setAlbums(prev => [newAlbum, ...prev]);
      return { success: true, album: newAlbum };
    } catch (error) {
      console.error('Error creating album:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getAlbum = async (id) => {
    try {
      console.log('Fetching album with ID:', id);
      
      const { data, error } = await supabase
        .from('albums_ab2024')
        .select(`
          *,
          photos:photos_ab2024(*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching album:', error);
        throw error;
      }

      console.log('Album data fetched:', data);

      return {
        id: data.id,
        shareId: data.share_id,
        title: data.title,
        description: data.description,
        eventDate: data.event_date,
        isPublic: data.is_public,
        ownerId: data.owner_id,
        ownerName: data.owner_name,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        photos: data.photos.map(photo => ({
          id: photo.id,
          url: photo.url,
          name: photo.name,
          size: photo.size,
          type: photo.type,
          uploaderName: photo.uploader_name,
          uploaderId: photo.uploader_id,
          uploadedAt: photo.uploaded_at
        }))
      };
    } catch (error) {
      console.error('Error fetching album:', error);
      return null;
    }
  };

  const getAlbumByShareId = async (shareId) => {
    try {
      const { data, error } = await supabase
        .from('albums_ab2024')
        .select(`
          *,
          photos:photos_ab2024(*)
        `)
        .eq('share_id', shareId)
        .eq('is_public', true)
        .single();

      if (error) throw error;

      return {
        id: data.id,
        shareId: data.share_id,
        title: data.title,
        description: data.description,
        eventDate: data.event_date,
        isPublic: data.is_public,
        ownerId: data.owner_id,
        ownerName: data.owner_name,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        photos: data.photos.map(photo => ({
          id: photo.id,
          url: photo.url,
          name: photo.name,
          size: photo.size,
          type: photo.type,
          uploaderName: photo.uploader_name,
          uploaderId: photo.uploader_id,
          uploadedAt: photo.uploaded_at
        }))
      };
    } catch (error) {
      console.error('Error fetching public album:', error);
      return null;
    }
  };

  const getUserAlbums = () => {
    return albums;
  };

  const addPhotoToAlbum = async (albumIdentifier, photoData) => {
    try {
      setLoading(true);
      console.log('Starting photo upload...', { albumIdentifier, photoData });

      const file = photoData.file;
      if (!file) {
        throw new Error('No file provided');
      }

      // Check file size - 500MB limit but don't throw error, just log warning
      const maxSize = 500 * 1024 * 1024; // 500MB in bytes
      if (file.size > maxSize) {
        console.warn(`File ${file.name} exceeds 500MB limit (${(file.size / 1024 / 1024).toFixed(2)}MB), skipping...`);
        return { success: false, error: 'File too large', silent: true };
      }

      // Create unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `photos/${fileName}`;

      console.log('Uploading to storage:', filePath, 'Size:', (file.size / 1024 / 1024).toFixed(2) + 'MB');

      // Upload to Supabase Storage with increased timeout
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('photos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        return { success: false, error: uploadError.message, silent: true };
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('photos')
        .getPublicUrl(filePath);

      console.log('File uploaded, public URL:', publicUrl);

      // UNIFIED LOGIC: Always treat identifier as shareId first, then fallback to numeric ID
      let albumId;

      // Check if this is a UUID (share ID) - UUIDs are exactly 36 characters with specific format
      const isShareId = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(albumIdentifier);

      console.log('Identifier analysis:', {
        albumIdentifier,
        isShareId,
        isNumeric: !isNaN(albumIdentifier) && !isShareId,
        type: typeof albumIdentifier
      });

      if (isShareId) {
        // This is a share ID, convert to album ID (WORKING PATH)
        console.log('Converting shareId to albumId:', albumIdentifier);
        
        const { data: albumData, error: albumError } = await supabase
          .from('albums_ab2024')
          .select('id, owner_id, is_public')
          .eq('share_id', albumIdentifier)
          .single(); // Remove is_public filter to allow owner uploads

        if (albumError || !albumData) {
          console.error('Album lookup error:', albumError);
          return { success: false, error: 'Album not found', silent: true };
        }

        albumId = albumData.id;
        console.log('Found albumId from shareId:', albumId);

        // Check permissions: owner can always upload, others need public album
        if (user && albumData.owner_id !== user.id && !albumData.is_public) {
          return { success: false, error: 'No permission to upload', silent: true };
        }
      } else {
        // This should be a numeric album ID, use it directly
        console.log('Using direct albumId:', albumIdentifier);
        
        // Ensure it's a number
        albumId = typeof albumIdentifier === 'string' ? parseInt(albumIdentifier, 10) : albumIdentifier;
        
        if (isNaN(albumId) || albumId <= 0) {
          return { success: false, error: 'Invalid album identifier', silent: true };
        }

        // Verify the album exists and the user has permission
        console.log('Verifying album exists:', albumId);
        
        const { data: albumExists, error: verifyError } = await supabase
          .from('albums_ab2024')
          .select('id, owner_id, is_public')
          .eq('id', albumId)
          .single();

        if (verifyError || !albumExists) {
          console.error('Album verification failed:', verifyError);
          return { success: false, error: 'Album not found', silent: true };
        }

        // Check if user has permission to upload (owner or public album)
        if (user && albumExists.owner_id !== user.id && !albumExists.is_public) {
          return { success: false, error: 'No permission to upload', silent: true };
        }

        console.log('Album verification passed:', albumExists);
      }

      console.log('Final albumId for insert:', albumId);

      // Insert photo record
      const { data: photoRecord, error: insertError } = await supabase
        .from('photos_ab2024')
        .insert({
          album_id: albumId,
          url: publicUrl,
          name: file.name,
          size: file.size,
          type: file.type,
          uploader_name: photoData.uploaderName || 'Anonymous',
          uploader_id: user?.id || null
        })
        .select()
        .single();

      if (insertError) {
        console.error('Database insert error:', insertError);
        return { success: false, error: `Failed to save photo: ${insertError.message}`, silent: true };
      }

      console.log('Photo saved successfully:', photoRecord);

      // Refresh albums if user is logged in
      if (user) {
        await fetchUserAlbums();
      }

      return { success: true, photo: photoRecord };
    } catch (error) {
      console.error('Error uploading photo:', error);
      return { success: false, error: error.message, silent: true };
    } finally {
      setLoading(false);
    }
  };

  const deletePhotoFromAlbum = async (photoId) => {
    try {
      setLoading(true);
      console.log('Deleting photo with ID:', photoId);

      // First get the photo to get the file path
      const { data: photoData, error: fetchError } = await supabase
        .from('photos_ab2024')
        .select('url, album_id')
        .eq('id', photoId)
        .single();

      if (fetchError || !photoData) {
        throw new Error('Photo not found');
      }

      // Extract file path from URL for storage deletion
      const url = photoData.url;
      const filePath = url.split('/').pop(); // Get filename from URL
      const fullPath = `photos/${filePath}`;

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('photos')
        .remove([fullPath]);

      if (storageError) {
        console.warn('Storage deletion failed:', storageError);
        // Continue with database deletion even if storage fails
      }

      // Delete from database
      const { error: deleteError } = await supabase
        .from('photos_ab2024')
        .delete()
        .eq('id', photoId);

      if (deleteError) {
        throw deleteError;
      }

      console.log('Photo deleted successfully');

      // Refresh albums if user is logged in
      if (user) {
        await fetchUserAlbums();
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting photo:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const deleteAlbum = async (albumId) => {
    try {
      setLoading(true);

      // Delete album (photos will be deleted by CASCADE)
      const { error } = await supabase
        .from('albums_ab2024')
        .delete()
        .eq('id', albumId)
        .eq('owner_id', user.id);

      if (error) throw error;

      // Update local state
      setAlbums(prev => prev.filter(album => album.id !== albumId));
      return { success: true };
    } catch (error) {
      console.error('Error deleting album:', error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    albums,
    loading,
    createAlbum,
    getAlbum,
    getAlbumByShareId,
    getUserAlbums,
    addPhotoToAlbum,
    deletePhotoFromAlbum,
    deleteAlbum,
    fetchUserAlbums
  };

  return (
    <AlbumContext.Provider value={value}>
      {children}
    </AlbumContext.Provider>
  );
};